import { Helmet } from 'react-helmet'
import React from 'react'
import { useAppSelector } from '../../state/hooks'

interface MenuSeoProps {
	seo?: {
		metaTitle?: string
		metaDescription?: string
		cuisineType?: string
		priceRange?: string
	}
	restaurantSlug: string
}

export default function MenuSeo({ seo, restaurantSlug }: MenuSeoProps) {
	const convertToTitleCase = str => {
		return str.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
	}
	const { loadedData } = useAppSelector(state => state.data)

	const orgName = loadedData[global.org]?.name || 'Restaurant'
	const restaurantName = restaurantSlug ? convertToTitleCase(restaurantSlug) : null
	const title = seo?.metaTitle
		? seo.metaTitle
		: restaurantName
			? `${restaurantName} - Menu | ${orgName}`
			: `${orgName} - Menu`
	const description = seo?.metaDescription
		? seo.metaDescription
		: restaurantName
			? `View the full menu at ${restaurantName} - ${orgName}`
			: `View the full menu at ${orgName}`
	const orgURL = loadedData[global.org].webURL
		? loadedData[global.org].webURL.replace(/^(http:\/\/|https:\/\/)/, '')
		: ''
	const canonicalUrl = `${orgURL}/menu/${restaurantSlug}`

	// Schema markup data
	const schemaData = {
		'@context': 'https://schema.org',
		'@type': 'Restaurant,',
		name: title,
		description: description,
		url: canonicalUrl,
		cuisineType: seo?.cuisineType || '',
		priceRange: seo?.priceRange || '',
	}

	return (
		<Helmet>
			{/* Basic Meta Tags */}
			<title>{title}</title>
			<meta name="description" content={description} />
			<meta charSet="utf-8" />
			<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
			<link rel="canonical" href={canonicalUrl} />

			{/* Open Graph Meta Tags */}
			<meta property="og:title" content={title} />
			<meta property="og:description" content={description} />
			<meta property="og:type" content="website" />
			<meta property="og:url" content={canonicalUrl} />
			<meta property="og:site_name" content={orgName} />

			{/* Twitter Card Meta Tags */}
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={title} />
			<meta name="twitter:description" content={description} />

			{/* Additional Meta Tags */}
			<meta name="robots" content="index, follow" />
			<meta name="theme-color" content="#ffffff" />
			<meta name="mobile-web-app-capable" content="yes" />
			<meta name="apple-mobile-web-app-capable" content="yes" />
			<meta name="apple-mobile-web-app-status-bar-style" content="default" />
			<meta name="apple-mobile-web-app-title" content={orgName} />

			{/* Schema.org JSON-LD */}
			<script type="application/ld+json">{JSON.stringify(schemaData)}</script>
		</Helmet>
	)
}
